import { css } from '@emotion/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  InputCheckboxControlled,
  InputDropdownListControlled,
  InputPhoneNumberControlled,
  InputRadioGroupControlled,
  InputTextControlled,
} from '@nexans/react-form'
import { Button, DropdownListItemText, ServiceLogo, Stack, Typography } from '@nexans/react-ui-kit'
import { type MouseEvent } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { InputAddressSearchControlled } from '@/components/forms/InputAddressSearchControlled'
import { getCguUrl, resolveCguLang } from '@/config/legal'
import { getMobiwayCountry } from '@/lib/axios'
import { ROUTER_PUBLIC_LINKS } from '@/router/config'
import { useAppStore } from '@/stores/appStore'
import { AvailableCountry as ApiAvailableCountry } from '@/types/openapi'
import { AvailableCountry, Civility, CompanySize, Language } from '@/types/openapi-public'

import { useRegisterUser } from '../api/useRegisterUser'
import { RegistrationFormSchema, type RegistrationFormValues } from '../schemas'

import { AuthFooter } from './AuthFooter'
import { AuthOtpState } from './CodeVerification'

export function Registration() {
  const { t, i18n } = useTranslation(['auth', 'common', 'enums', 'errors'])
  const navigate = useNavigate()
  const cguCountry = getMobiwayCountry() ?? ApiAvailableCountry.FR
  const cguUrl = getCguUrl(cguCountry, resolveCguLang(cguCountry, i18n.language?.split('-')[0]))
  const setSessionId = useAppStore((state) => state.actions.setSessionId)

  const {
    control,
    formState: { dirtyFields, errors, isValid },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<RegistrationFormValues>({
    mode: 'onChange',
    resolver: zodResolver(RegistrationFormSchema),
    defaultValues: {
      hasAcceptedTerms: false,
      preferredLanguage: Language.FR,
      hasConsentedMarketing: false,
      // InputPhoneNumber emits an empty onChange on mount; an empty default keeps the field pristine instead of dirty
      phoneNumber: '',
    },
  })

  // Same reason: the mount-time onChange also validates the field, so only surface its error once the user edited it
  const phoneNumberError = dirtyFields.phoneNumber ? errors.phoneNumber : undefined

  const { mutate: registerUser, isPending } = useRegisterUser({
    config: {
      onSuccess: (data) => {
        setSessionId(data.sessionId)
        const otpState: AuthOtpState = { email: getValues('email'), origin: 'register' }
        navigate(ROUTER_PUBLIC_LINKS.authOtp.path, { state: otpState })
      },
    },
  })

  const companySizeOptions = Object.keys(CompanySize).map((key) => ({
    value: key,
    label: t(`enums:enum.company_size.${key}`),
  }))

  const civilityOptions = Object.values(Civility).map((key) => ({
    value: key,
    label: t(`enums:enum.civility.${key}`),
  }))

  const languageOptions = [Language.FR, Language.EN].map((key) => ({
    value: key,
    label: t(`enums:enum.language.${key}`),
  }))

  const onSubmit = (data: RegistrationFormValues) => {
    const { hasAcceptedTerms: _hasAcceptedTerms, ...command } = data

    registerUser(command)
  }

  const handleCivilityClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target

    if (target instanceof HTMLInputElement) {
      const civility = Object.values(Civility).find((value) => value === target.value)

      if (civility) {
        queueMicrotask(() => {
          setValue('civility', civility, { shouldDirty: true, shouldValidate: true })
        })
      }
    }
  }

  return (
    <Stack justify="space-between" spacing="xl" fullHeight fullWidth>
      <ServiceLogo css={{ alignSelf: 'center', marginTop: 24 }} iconSrc={undefined} iconSrcScale={1} size={40} title="Mobiway™" />

      <Stack css={{ flex: 1 }} justify="center" spacing="lg" fullWidth>
        <Typography css={{ marginBottom: 24 }} textAlign="center" variant="h2">
          {t('auth:labels.register.title')}
        </Typography>

        <Typography textAlign="justify" variant="subtitle1">
          {t('auth:labels.register.introduction')}
        </Typography>

        <Typography css={{ marginTop: 24 }} variant="subtitle1">
          {t('auth:labels.register.company_data')}
        </Typography>

        <InputTextControlled
          control={control}
          errorText={errors.companyName?.message ? t(`errors:${errors.companyName?.message}`) : ''}
          gutterBottom={!!errors.companyName}
          label={t('auth:labels.register.company_name')}
          name="companyName"
          fullWidth
          required
        />

        <InputDropdownListControlled
          clearable={false}
          control={control}
          errorText={errors.companySize?.message ? t(`errors:${errors.companySize?.message}`) : ''}
          gutterBottom={!!errors.companySize}
          keyValue="value"
          label={t('auth:labels.register.company_size')}
          name="companySize"
          options={companySizeOptions}
          renderItem={(item) => <DropdownListItemText label={item.label} value={item.value} />}
          renderLabel={(item) => item[0]?.label ?? ''}
          fullWidth
          required
        />

        <InputAddressSearchControlled
          control={control}
          errorText={errors.companyAddress?.message ? t(`errors:${errors.companyAddress?.message}`) : ''}
          gutterBottom={!!errors.companyAddress}
          label={t('auth:labels.register.address')}
          name="companyAddress"
          required
        />

        <Typography css={{ marginTop: 24 }} variant="subtitle1">
          {t('auth:labels.register.personal_data')}
        </Typography>

        <Stack>
          <InputRadioGroupControlled
            control={control}
            direction="horizontal"
            errorText={errors.civility?.message ? t(`errors:${errors.civility.message}`) : ''}
            name="civility"
            options={civilityOptions}
            onClickCapture={handleCivilityClick}
          />

          <InputTextControlled
            control={control}
            errorText={errors.lastName?.message ? t(`errors:${errors.lastName?.message}`) : ''}
            gutterBottom={!!errors.lastName}
            label={t('auth:labels.register.last_name')}
            name="lastName"
            fullWidth
            required
          />
        </Stack>

        <InputTextControlled
          control={control}
          errorText={errors.firstName?.message ? t(`errors:${errors.firstName?.message}`) : ''}
          gutterBottom={!!errors.firstName}
          label={t('auth:labels.register.first_name')}
          name="firstName"
          fullWidth
          required
        />

        <InputTextControlled
          control={control}
          errorText={errors.email?.message ? t(`errors:${errors.email?.message}`) : ''}
          gutterBottom={!!errors.email}
          label={t('auth:labels.register.email')}
          name="email"
          fullWidth
          required
        />

        <InputPhoneNumberControlled
          control={control}
          defaultCountry={getMobiwayCountry()?.toLowerCase() as Lowercase<AvailableCountry> | undefined}
          errorText={phoneNumberError?.message ? t(`errors:${phoneNumberError.message}`) : ''}
          gutterBottom={!!phoneNumberError}
          label={t('auth:labels.register.phone_number')}
          name="phoneNumber"
          fullWidth
          required
        />

        <InputDropdownListControlled
          clearable={false}
          control={control}
          errorText={errors.preferredLanguage?.message ? t(`errors:${errors.preferredLanguage?.message}`) : ''}
          gutterBottom={!!errors.preferredLanguage}
          keyValue="value"
          label={t('auth:labels.register.preferred_language')}
          name="preferredLanguage"
          options={languageOptions}
          renderItem={(item) => <DropdownListItemText label={item.label} value={item.value} />}
          renderLabel={(item) => item[0]?.label ?? ''}
          fullWidth
          required
        />

        <InputCheckboxControlled
          control={control}
          gutterBottom={false}
          label={
            <Trans
              components={{
                termsLink: (
                  <a
                    css={styles.termsLink}
                    href={cguUrl}
                    rel="noreferrer"
                    target="_blank"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {null}
                  </a>
                ),
              }}
              i18nKey="auth:labels.register.terms_consent"
            />
          }
          labelPosition="end"
          name="hasAcceptedTerms"
          fullwidth
          required
        />

        <InputCheckboxControlled
          control={control}
          gutterBottom={!!errors.hasConsentedMarketing}
          label={t('auth:labels.register.marketing_consent')}
          labelPosition="end"
          name="hasConsentedMarketing"
          fullwidth
        />

        <Stack direction="vertical" mt="xl" spacing="xl" fullWidth>
          <Button
            color="gradient"
            css={styles.gradientButton}
            disabled={!isValid || isPending}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            {t('auth:actions.create_account')}
          </Button>
        </Stack>
      </Stack>

      <AuthFooter />
    </Stack>
  )
}

const styles = {
  gradientButton: css({
    '&:not(:disabled)': { filter: 'drop-shadow(0 16px 32px rgba(255, 0, 0, 0.35))' },
  }),
  termsLink: css({
    color: '#0000ee',
    textDecoration: 'underline',
  }),
}
